import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ImageContext } from "../../../Context/ImageContext";
import { useNavigate } from "react-router-dom";
import { getPerspectiveTransformMatrix } from "../../../lib/perspective-transform";
import DraggableHandle from "../../../Desktop/DraggableHandle";

function DoorCrop({ handleBack }) {
  const { cropImage, setSelectedDoor } = useContext(ImageContext);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const initialDoorWidth = 225;
  const initialDoorHeight = 100;
  const [image, setImage] = useState();
  const [bgKonvaImage, setBgKonvaImage] = useState(null);
  const [draggingHandle, setDraggingHandle] = useState(null);
  const [isDraggingDoor, setIsDraggingDoor] = useState(false);
  const [dragStartOffset, setDragStartOffset] = useState(null);
  const [imageTransform, setImageTransform] = useState(
    "matrix3d(1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1)"
  );
  const [points, setPoints] = useState([
    { x: 103, y: 167 },
    { x: 206, y: 167 },
    { x: 206, y: 225 },
    { x: 103, y: 226 },
  ]);

  useEffect(() => {
    if (image && points.length === 4) {
      const srcPoints = [
        { x: 0, y: 0 },
        { x: initialDoorWidth, y: 0 },
        { x: initialDoorWidth, y: initialDoorHeight },
        { x: 0, y: initialDoorHeight },
      ];
      const transform = getPerspectiveTransformMatrix(srcPoints, points);
      setImageTransform(transform);
    }
  }, [points, image]);

  const getMousePositionInContainer = (clientX, clientY) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(clientY - rect.top, rect.height)),
    };
  };

  const handleMouseDownOnHandle = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches?.[0] || e;
    const pos = getMousePositionInContainer(touch.clientX, touch.clientY);
    if (!pos) return;
    setDraggingHandle(id);
    setIsDraggingDoor(false);
  };

  const handleMouseDownOnDoor = (e) => {
    if (draggingHandle !== null) return;
    e.preventDefault();
    const touch = e.touches?.[0] || e;
    const pos = getMousePositionInContainer(touch.clientX, touch.clientY);
    if (!pos || points.length !== 4) return;
    setDragStartOffset({ x: pos.x - points[0].x, y: pos.y - points[0].y });
    setIsDraggingDoor(true);
  };

  const handleMouseMove = useCallback(
    (e) => {
      const touch = e.touches?.[0] || e;
      const pos = getMousePositionInContainer(touch.clientX, touch.clientY);
      if (!pos) return;

      setPoints((prevPoints) => {
        if (draggingHandle !== null) {
          const newPoints = [...prevPoints];
          newPoints[draggingHandle] = { x: pos.x, y: pos.y };
          return newPoints;
        } else if (isDraggingDoor && dragStartOffset) {
          const dx = pos.x - (prevPoints[0].x + dragStartOffset.x);
          const dy = pos.y - (prevPoints[0].y + dragStartOffset.y);
          return prevPoints.map((pt) => ({ x: pt.x + dx, y: pt.y + dy }));
        }
        return prevPoints;
      });
    },
    [draggingHandle, isDraggingDoor, dragStartOffset]
  );

  const handleMouseUp = useCallback(() => {
    setDraggingHandle(null);
    setIsDraggingDoor(false);
    setDragStartOffset(null);
  }, []);

  useEffect(() => {
    if (!image) return;
    const move = (e) => handleMouseMove(e);
    const up = () => handleMouseUp();
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", up);
    return () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", up);
    };
  }, [handleMouseMove, handleMouseUp, image]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPointsByScreenSize = (cropImage) => {
    if (cropImage.includes("Platinum")) {
      return [
        { xPercent: 48, y: 161.71875 },
        { xPercent: 79, y: 162.6875 },
        { xPercent: 79, y: 218.71875 },
        { xPercent: 48, y: 217.6875 },
      ];
    } else if (cropImage.includes("Roller Door")) {
      return [
        { xPercent: 53.9, y: 161.953125 },
        { xPercent: 92.6, y: 163.78125 },
        { xPercent: 92.6, y: 242.78125 },
        { xPercent: 53.9, y: 242.953125 },
      ];
    } else if (cropImage.includes("section_hampton_windspray")) {
      return [
        { xPercent: 45.5, y: 135.953125 },
        { xPercent: 85, y: 134.78125 },
        { xPercent: 85, y: 248.78125 },
        { xPercent: 45.5, y: 243.953125 },
      ];
    } else if (cropImage.includes("Sectional Door")) {
      return [
        { xPercent: 29, y: 178.953125 },
        { xPercent: 53, y: 178.78125 },
        { xPercent: 53, y: 239.78125 },
        { xPercent: 29, y: 240.953125 },
      ];
    } else if (cropImage.includes("sectional_madison_monument_smooth_home")) {
      return [
        { xPercent: 40.5, y: 201.953125 },
        { xPercent: 63.7, y: 191.78125 },
        { xPercent: 63.9, y: 262.78125 },
        { xPercent: 40.5, y: 249.953125 },
      ];
    } else if (cropImage.includes("sectional_madison_nightsky")) {
      return [
        { xPercent: 46, y: 126.953125 },
        { xPercent: 84, y: 130.78125 },
        { xPercent: 84, y: 248.78125 },
        { xPercent: 46, y: 247.953125 },
      ];
    } else if (cropImage.includes("sectional_tuscan_dover_white")) {
      return [
        { xPercent: 48.5, y: 136.953125 },
        { xPercent: 78, y: 136.78125 },
        { xPercent: 78, y: 195.78125 },
        { xPercent: 48.5, y: 196.953125 },
      ];
    } else if (cropImage.includes("Selections")) {
      return [
        { xPercent: 47, y: 170.953125 },
        { xPercent: 73, y: 170.78125 },
        { xPercent: 73.5, y: 225.78125 },
        { xPercent: 47, y: 225.953125 },
      ];
    }

    // Default points for user-uploaded images (center of image)
    return [
      { xPercent: 30, y: 140 },
      { xPercent: 70, y: 140 },
      { xPercent: 70, y: 220 },
      { xPercent: 30, y: 220 },
    ];
  };

  const convertPointsToPixels = (percentPoints, containerWidth) => {
    return percentPoints.map((pt) => ({
      x: (pt.xPercent / 100) * containerWidth,
      y: pt.y,
    }));
  };

  useEffect(() => {
    if (cropImage && width > 0) {
      setBgKonvaImage(cropImage);
      setImage(cropImage);
      const percentPoints = getPointsByScreenSize(cropImage);
      const pixelPoints = convertPointsToPixels(percentPoints, width);
      setPoints(pixelPoints);
    }
  }, [cropImage, width]);

  const saveSelectionPosition = () => {
    setSelectedDoor(points);
    navigate("/customise_door");
  };

  return (
    <>
      <p>Select your door area</p>
      <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
        Drag the corners to mark where your garage door is located. You can also drag the entire area to reposition it.
      </p>
      <div
        ref={containerRef}
        className="overflow-hidden position-relative"
        id="image-display-area"
        style={{ width: "100%", height: "300px" }}
      >
        {bgKonvaImage && (
          <img
            src={bgKonvaImage}
            alt="Background"
            style={{ width: "100%", height: "100%", display: "block" }}
          />
        )}

        {image && (
          <div
            id="door-overlay-crop"
            className="position-absolute"
            style={{
              width: `${initialDoorWidth}px`,
              height: `${initialDoorHeight}px`,
              transform: imageTransform,
              transformOrigin: "0 0",
              cursor: isDraggingDoor ? "grabbing" : "grab",
              top: 0,
              touchAction: "none",
            }}
            onMouseDown={handleMouseDownOnDoor}
            onTouchStart={handleMouseDownOnDoor}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "#00000069",
                position: "relative",
              }}
            >
              <img
                src={image}
                alt="Selected door"
                style={{
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
        )}

        {image &&
          points.map((point, index) => (
            <DraggableHandle
              key={index}
              id={index}
              x={point.x}
              y={point.y}
              onMouseDown={handleMouseDownOnHandle}
              onTouchStart={(e) => handleMouseDownOnHandle(e, index)}
              size={24}
            />
          ))}
      </div>
      <button
        className="btn continue mb-3 mt-5"
        onClick={saveSelectionPosition}
      >
        Select and Continue
      </button>
      <button className="btn back mb-3" onClick={handleBack}>
        Back
      </button>
    </>
  );
}

export default DoorCrop;
